import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useLabOrders } from './useLabOrders';
import { useLabOrderActions } from './useLabOrderActions';
import { useLaboratories } from '../../hooks/useLaboratories';
import { useProfessionals } from '../../hooks/useProfessionals';
import { LAB_ORDER_STATUS, isLabOrderLate, parseApiDate, toMessageText } from '../labOrderStatus';

// Orchestrates the lab orders screen. Consumed by the web and mobile versions,
// which only handle presentation.
export function useLabOrdersScreen() {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  const [laboratoryId, setLaboratoryId] = useState('');
  const [dentistId, setDentistId] = useState('');
  const [status, setStatus] = useState('');
  const [onlyLate, setOnlyLate] = useState(false);

  const {
    labOrders: allLabOrders,
    isLoading: isListLoading,
    error: listError,
    refetch: getLabOrders,
  } = useLabOrders();
  const { laboratories, isLoading: isLaboratoriesLoading } = useLaboratories();
  const { professionals, isLoading: isProfessionalsLoading } = useProfessionals();
  const { changeStatus, remove, isLoading: isActionLoading } = useLabOrderActions();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSelectedLabOrder, setMenuSelectedLabOrder] = useState(null);

  const [openFormModal, setOpenFormModal] = useState(false);
  const [labOrderToEdit, setLabOrderToEdit] = useState(null);

  const [statusDialog, setStatusDialog] = useState(null);
  const [statusDate, setStatusDate] = useState(dayjs());
  const [statusExpectedReturnDate, setStatusExpectedReturnDate] = useState(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(toMessageText(listError));
    }
  }, [listError]);

  // The API returns every order; the screen filters in memory. The period never hides orders
  // waiting for pickup (no sent date yet) and is ignored by "Atrasados": a late work may have
  // been sent long before the selected period.
  const labOrders = useMemo(
    () =>
      allLabOrders.filter((labOrder) => {
        if (laboratoryId && labOrder.laboratoryId !== laboratoryId) return false;
        if (dentistId && labOrder.dentistId !== dentistId) return false;
        if (status && labOrder.status !== status) return false;
        if (onlyLate) return isLabOrderLate(labOrder);
        if (labOrder.status === LAB_ORDER_STATUS.TO_SEND) return true;

        const sentDate = parseApiDate(labOrder.sentDate);
        if (startDate && sentDate?.isBefore(dayjs(startDate), 'day')) return false;
        if (endDate && sentDate?.isAfter(dayjs(endDate), 'day')) return false;
        return true;
      }),
    [allLabOrders, startDate, endDate, laboratoryId, dentistId, status, onlyLate],
  );

  const lateCount = useMemo(() => allLabOrders.filter(isLabOrderLate).length, [allLabOrders]);

  const toSendCount = useMemo(
    () => allLabOrders.filter((labOrder) => labOrder.status === LAB_ORDER_STATUS.TO_SEND).length,
    [allLabOrders],
  );

  const totalAmount = useMemo(
    () =>
      labOrders
        .filter((labOrder) => labOrder.status !== LAB_ORDER_STATUS.CANCELED)
        .reduce((total, labOrder) => total + (Number(labOrder.amount) || 0), 0),
    [labOrders],
  );

  const showSuccess = useCallback((message) => {
    setShowSuccessMessage(true);
    setSuccessMessage(message);
  }, []);

  const showApiError = useCallback(
    (returnFromApi) => {
      setShowErrorMessage(true);
      setErrorMessage(toMessageText(returnFromApi.response?.data));
      console.error('ops! ocorreu um erro' + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate('/expired-session');
      }
    },
    [navigate],
  );

  const clearFilters = useCallback(() => {
    setStartDate(dayjs().startOf('month'));
    setEndDate(dayjs().endOf('month'));
    setLaboratoryId('');
    setDentistId('');
    setStatus('');
    setOnlyLate(false);
  }, []);

  const openMenu = useCallback((event, labOrder) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedLabOrder(labOrder);
  }, []);

  // Keeps the selected order: the status and delete dialogs still need it
  const closeMenu = useCallback(() => setMenuAnchorEl(null), []);

  const addLabOrder = useCallback(() => {
    setLabOrderToEdit(null);
    setOpenFormModal(true);
  }, []);

  const editLabOrder = useCallback(() => {
    setLabOrderToEdit(menuSelectedLabOrder);
    closeMenu();
    setOpenFormModal(true);
  }, [menuSelectedLabOrder, closeMenu]);

  const closeFormModal = useCallback(() => {
    setOpenFormModal(false);
    setLabOrderToEdit(null);
  }, []);

  const saveSuccess = useCallback(
    (returnFromApi) => {
      showSuccess(toMessageText(returnFromApi.data, 'Envio salvo com sucesso.'));
      closeFormModal();
      getLabOrders();
    },
    [showSuccess, closeFormModal, getLabOrders],
  );

  const clickChangeStatus = useCallback(
    (targetStatus) => {
      closeMenu();
      setStatusDate(dayjs());
      setStatusExpectedReturnDate(null);
      setStatusDialog(targetStatus);
    },
    [closeMenu],
  );

  const closeStatusDialog = useCallback(() => setStatusDialog(null), []);

  const confirmChangeStatus = useCallback(() => {
    if (!menuSelectedLabOrder || !statusDialog) return;

    const date = statusDate ? dayjs(statusDate).format('DD/MM/YYYY') : null;
    const expectedReturnDate = statusExpectedReturnDate
      ? dayjs(statusExpectedReturnDate).format('DD/MM/YYYY')
      : null;
    const labOrderId = menuSelectedLabOrder.id;
    setStatusDialog(null);
    changeStatus(labOrderId, statusDialog, date, expectedReturnDate)
      .then((returnFromApi) => {
        showSuccess(toMessageText(returnFromApi.data, 'Status atualizado com sucesso.'));
        getLabOrders();
      })
      .catch(showApiError);
  }, [
    menuSelectedLabOrder,
    statusDialog,
    statusDate,
    statusExpectedReturnDate,
    changeStatus,
    showSuccess,
    showApiError,
    getLabOrders,
  ]);

  const clickDelete = useCallback(() => {
    closeMenu();
    setOpenDeleteModal(true);
  }, [closeMenu]);

  const closeDeleteModal = useCallback(() => setOpenDeleteModal(false), []);

  const confirmDelete = useCallback(() => {
    if (!menuSelectedLabOrder) return;

    const labOrderId = menuSelectedLabOrder.id;
    setOpenDeleteModal(false);
    remove(labOrderId)
      .then((returnFromApi) => {
        showSuccess(toMessageText(returnFromApi.data, 'Envio excluído com sucesso.'));
        setMenuSelectedLabOrder(null);
        getLabOrders();
      })
      .catch(showApiError);
  }, [menuSelectedLabOrder, remove, showSuccess, showApiError, getLabOrders]);

  return {
    labOrders,
    lateCount,
    toSendCount,
    totalAmount,
    isLoading: isListLoading || isActionLoading || isLaboratoriesLoading || isProfessionalsLoading,

    laboratories,
    professionals,

    startDate,
    setStartDate,
    endDate,
    setEndDate,
    laboratoryId,
    setLaboratoryId,
    dentistId,
    setDentistId,
    status,
    setStatus,
    onlyLate,
    setOnlyLate,
    clearFilters,

    menuAnchorEl,
    menuSelectedLabOrder,
    openMenu,
    closeMenu,

    openFormModal,
    labOrderToEdit,
    addLabOrder,
    editLabOrder,
    closeFormModal,
    saveSuccess,

    statusDialog,
    statusDate,
    setStatusDate,
    statusExpectedReturnDate,
    setStatusExpectedReturnDate,
    clickChangeStatus,
    closeStatusDialog,
    confirmChangeStatus,

    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,

    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  };
}
